<template>
  <v-card class="mb-4">
    <v-card-title>
      <span class="bold table-card-title pl-2"> Resultados por produto </span>
      <v-spacer />
      <export-data :data-fetcher="getDataToExport" :filename="exportFileName" ghost />
      <headers-data-table-settings
        :headers.sync="headers"
        :default-headers="getDefaultHeadersOptions()"
        headers-settings-name="maria-results-by-products-headers-configs"
      />
    </v-card-title>
    <v-data-table :headers="computedHeaders" :items="formatDataTable" disable-pagination calculate-widths hide-default-footer :loading="loading">
      <template v-slot:header.received="{ header }">
        {{ header.text }}

        <v-tooltip bottom max-width="192px">
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <mf-icon small color="cinza-0">info</mf-icon>
            </span>
          </template>
          <span>Cliente recebeu disparo e comprou o produto.</span>
        </v-tooltip>
      </template>
      <template v-slot:header.sent="{ header }">
        {{ header.text }}

        <v-tooltip bottom max-width="192px">
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <mf-icon v-if="usedProductVariable" small color="cinza-0">info</mf-icon>
              <mf-icon v-else small fill color="vermelho-d20">warning</mf-icon>
            </span>
          </template>
          <span v-if="usedProductVariable">Clientes que receberam algum disparo, por qualquer canal, contendo esse produto.</span>
          <span v-else>Não é possível identificar consumidores atingidos em campanhas disparadas sem variável de produto na mensagem.</span>
        </v-tooltip>
      </template>
      <template v-slot:header.upsell="{ header }">
        {{ header.text }}

        <v-tooltip bottom max-width="192px">
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <mf-icon small color="cinza-0">info</mf-icon>
            </span>
          </template>
          <span>Cliente recebeu disparo e comprou um produto da campanha pela primeira vez.</span>
        </v-tooltip>
      </template>
      <template v-slot:header.impact="{ header }">
        {{ header.text }}

        <v-tooltip bottom max-width="192px">
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <mf-icon small color="cinza-0">info</mf-icon>
            </span>
          </template>
          <span>Impacto desta campanha na quantidade deste produto que foi vendida no período.</span>
        </v-tooltip>
      </template>

      <template v-slot:item.code="{ item }">
        <a class="link" target="_blank" :href="`#/produtos/detalhes-produto/${item.code}`">{{ item.code }}</a>
      </template>
      <template v-slot:item.product="{ item }">
        {{ item.product }}
      </template>
      <template v-slot:item.upsell="{ item }">
        {{ item.upsell.toLocaleString('pt-BR') }}
        <mf-quick-actions
          v-if="item._id"
          :quick-action-group-id="`received-and-bought-${item._id}-first-time`"
          short-description="Clientes que receberam disparo e compraram o produto pela primeira vez"
          :long-description="`Clientes que receberam a campanha ${campaign.title} e compraram o produto ${item.product} pela primeira vez`"
          :segmentation-group="
            criteriaGroup(
              clientReceivedCampaignAndBougthTheProductFirstTimeCriteria(
                campaign._id,
                dateCriteriaFilter.formattedADayBeforeCampaignStart,
                item.code,
                formattedUnits
              )
            )
          "
        />
      </template>
      <template v-slot:item.tickets="{ item }">
        {{ item.tickets.toLocaleString('pt-BR') }}
      </template>
      <template v-slot:item.received="{ item }">
        {{ item.received.toLocaleString('pt-BR') }}
        <mf-quick-actions
          v-if="item._id"
          :quick-action-group-id="`received-and-bought-${item._id}`"
          short-description="Clientes que receberam disparo e compraram o produto"
          :long-description="`Clientes que receberam a campanha ${campaign.title} e compraram o produto ${item.product}`"
          :segmentation-group="
            criteriaGroup([
              clientReceivedCampaignAndBougthTheProductCriteria(campaign._id, item.code),
              isInUnitsCriteriaByProduct(formattedUnits, dateCriteriaFilter.formattedInitialDate, dateCriteriaFilter.formattedFinalDate, item.code),
            ])
          "
        />
      </template>
      <template v-slot:item.sent="{ item }">
        {{ item.sent.toLocaleString('pt-BR') }}
      </template>
      <template v-slot:item.sales="{ item }">
        {{ item.sales.toLocaleString('pt-BR') }}
      </template>
      <template v-slot:item.revenues="{ item }">
        {{ locale(item.revenues, 2) | localeString(2) | currency }}
      </template>
      <template v-slot:item.impact="{ item }">
        {{ locale(item.impact, 2) | localeString(2) | percent }}
      </template>

      <template v-slot:footer>
        <table :style="{ width: '100%' }">
          <tr>
            <td v-for="(header, index) in computedHeaders" :key="index" class="text-start" :style="{ width: `${header.width} !important` }">
              <span v-if="header.value === 'code'">
                <v-tooltip bottom max-width="192px">
                  <template v-slot:activator="{ on }">
                    <div class="d-flex align-center">
                      <span>Total:</span>
                      <span v-on="on">
                        <mf-icon small color="cinza-0" class="ml-1">info</mf-icon>
                      </span>
                    </div>
                  </template>
                  <span
                    >O total é calculado considerando clientes e cupons únicos. Um mesmo cliente pode comprar diferentes produtos, e um produto pode aparecer em
                    diversos cupons. Por isso a soma dos valores da tabela pode não ser igual ao total.</span
                  >
                </v-tooltip>
              </span>
              <span v-else-if="header.currency">
                {{ locale(totalResultsByProducts[header.value], 2) | localeString(2) | currency }}
              </span>
              <span v-else-if="header.percent">
                {{ locale(totalResultsByProducts[header.value], 2) | localeString(2) | percent }}
              </span>
              <span v-else>
                {{ totalResultsByProducts[header.value].toLocaleString('pt-BR') }}
              </span>
            </td>
          </tr>
        </table>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { putEmptyValue, localeString } from '@/config/helpers.js'
import {
  criteriaGroup,
  clientReceivedCampaignAndBougthTheProductCriteria,
  clientReceivedCampaignAndBougthTheProductFirstTimeCriteria,
  isInUnitsCriteriaByProduct,
} from '@/components/quick-actions/segmentationGroups.js'
import HeadersDataTableSettings from '@/components/HeadersDataTableSettings.vue'
import { DateTime } from 'luxon'
import cloneDeep from 'lodash/cloneDeep'

const defaultHeaders = [
  {
    text: 'Código',
    value: 'code',
    width: '8.5%',
    isActive: true,
    isDefault: true,
    isRequired: true,
  },
  {
    text: 'Produto',
    value: 'product',
    width: '24%',
    isActive: true,
    isDefault: true,
    isRequired: true,
  },
  {
    text: 'Convertidos',
    value: 'received',
    isActive: true,
    isDefault: true,
    isRequired: false,
  },
  {
    text: 'Atingidos',
    value: 'sent',
    isActive: true,
    isDefault: true,
    isRequired: false,
  },
  {
    text: 'Cupons',
    value: 'tickets',
    isActive: false,
    isDefault: false,
    isRequired: false,
  },
  {
    text: 'Qtd. vendas',
    value: 'sales',
    isActive: true,
    isDefault: true,
    isRequired: false,
  },
  {
    text: 'Faturamento',
    value: 'revenues',
    isActive: true,
    isDefault: true,
    isRequired: false,
    currency: true,
  },
  {
    text: 'Impacto',
    value: 'impact',
    isActive: true,
    isDefault: true,
    isRequired: false,
    percent: true,
  },
  {
    text: 'Upsell',
    value: 'upsell',
    isActive: true,
    isDefault: true,
    isRequired: false,
  },
]

export default {
  name: 'ResultsByProducts',

  components: {
    HeadersDataTableSettings,
    ExportData: () => import('@/components/export-data/ExportData.vue'),
  },

  props: {
    metrics: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: () => false,
    },
    campaign: {
      type: Object,
      default: () => {},
    },
    dateCriteriaFilter: {
      type: Object,
      default: () => {},
    },
    units: {
      type: Array,
      default: () => [],
    },
    unitsConfiguration: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    locale: localeString,
    headers: [],
  }),

  computed: {
    exportFileName() {
      return `${this.campaign.title}_resultados_por_produto`
    },
    campaignScheduleSettings() {
      return this.campaign.messages_dispatcher?.settings?.schedule_settings ?? []
    },
    usedProductVariable() {
      if (!this.campaignScheduleSettings?.length) return false
      return this.campaignScheduleSettings
        .map((scheduleSetting) => {
          return Object.values(scheduleSetting.channels)
            .map((channel) => {
              if (!channel?.message) return false
              // TODO: how to deal with email?
              const possibleKeysWithProductVariable = ['text', 'title', 'inner_message_text', 'inner_message_title']
              return possibleKeysWithProductVariable
                .map((key) => {
                  return this.isUsingProductVariable(channel.message[key])
                })
                .some((item) => item)
            })
            .some((item) => item)
        })
        .some((item) => item)
    },
    computedHeaders() {
      const sizeWithoutCodeAndProduct = 100 - (24 + 8.5)
      const headers = this.headers.filter((header) => header.isActive)
      const activableHeadersSize = headers.filter((header) => !header.isRequired).length
      return headers.map((header) => {
        return {
          ...header,
          width: header.width ? header.width : `${sizeWithoutCodeAndProduct / activableHeadersSize}%`,
        }
      })
    },
    formatDataTable() {
      if (!this.metrics?.table) return []

      return this.metrics?.table.map((product, index) => {
        return {
          _id: product.product._id,
          code: product.product.sku,
          product: product.product.name,
          sent: product.message_received?.clients,
          upsell: this.putEmptyValue(product.received_upsell?.clients, 0),
          received: product.received?.clients,
          tickets: product.received.tickets,
          sales: product.received.quantity,
          revenues: product.received.revenue,
          impact: this.putEmptyValue((product.received.quantity / product.total.quantity) * 100, 0),
        }
      })
    },
    totalResultsByProducts() {
      if (!this.metrics?.totals) return {}
      return {
        code: '',
        product: '',
        upsell: this.putEmptyValue(this.metrics?.totals?.received_upsell?.clients, 0),
        sent: this.putEmptyValue(this.metrics?.totals?.message_received?.clients, 0),
        received: this.putEmptyValue(this.metrics?.totals?.received?.clients, 0),
        tickets: this.putEmptyValue(this.metrics?.totals?.received?.tickets, 0),
        sales: this.putEmptyValue(this.metrics?.totals?.received?.quantity, 0),
        revenues: this.putEmptyValue(this.metrics?.totals?.received?.revenue, 0),
        impact: this.putEmptyValue((this.metrics?.totals?.received?.quantity / this.metrics?.totals?.total?.quantity) * 100, 0),
      }
    },
    sixMonthsAgoBeforeCampaignStarts() {
      const date = this.campaign.initial_date
      return DateTime.fromFormat(date.toString(), 'yyyy-MM-dd').plus({ months: -6 }).toFormat('yyyyMMdd')
    },
    formattedUnits() {
      return this.campaign.units.map((unit) => ({ dataType: unit.dataType, value: unit.id_erp }))
    },
  },

  mounted() {
    if (this.headers.length < 1) this.headers = cloneDeep(this.defaultHeaders)
  },

  methods: {
    putEmptyValue,
    criteriaGroup,
    clientReceivedCampaignAndBougthTheProductCriteria,
    clientReceivedCampaignAndBougthTheProductFirstTimeCriteria,
    isInUnitsCriteriaByProduct,
    getDataToExport() {
      const headers = this.computedHeaders.map((header) => header.text)
      const rows = this.formatDataTable.map((dataRow) => this.computedHeaders.map((header) => dataRow[header.value]))
      return {
        headers,
        rows,
      }
    },
    getDefaultHeadersOptions() {
      return cloneDeep(defaultHeaders)
    },
    isUsingProductVariable(message) {
      if (!message) return false
      const betterProductRegex = /\[melhor oferta\]/g
      return betterProductRegex.test(message)
    },
  },
}
</script>

<style></style>
